'use client';

import React, { useState } from 'react';
import {
  ChevronRight,
  ChevronDown,
  Clock,
  Sparkles,
  Edit2,
  Trash2,
  SlidersHorizontal,
  Scissors,
  GripVertical,
  Star
} from 'lucide-react';

export interface ServiceItem {
  id: string;
  name: string;
  description?: string;
  duration: number;
  cashPrice: number;
  listPrice: number;
  depositRequired?: boolean;
  depositType?: string;
  depositAmount?: number;
  bufferBefore?: number;
  bufferAfter?: number;
  modality?: string;
  pauseDuration?: number;
  imageUrl?: string | null;
  category: string;
  categoryId?: string | null;
  orderIndex?: number;
  active: boolean;
  isFeatured?: boolean;
  staff?: { id: string; name: string; colorHex: string; roleTitle?: string }[];
}

export interface CategoryItem {
  id: string;
  name: string;
  coverUrl?: string | null;
  orderIndex?: number;
  _count?: { services: number };
}

interface ServicesCategoryAccordionProps {
  categories: CategoryItem[];
  services: ServiceItem[];
  searchQuery: string;
  onEditService: (service: ServiceItem) => void;
  onDeleteService: (serviceId: string) => void;
  onToggleFeaturedService?: (service: ServiceItem) => void;
  onEditCategory?: (category: CategoryItem) => void;
  onReorderCategories?: (newCategories: CategoryItem[]) => Promise<void> | void;
  onReorderServices?: (newServices: ServiceItem[]) => Promise<void> | void;
}

export function ServicesCategoryAccordion({
  categories,
  services,
  searchQuery,
  onEditService,
  onDeleteService,
  onToggleFeaturedService,
  onEditCategory,
  onReorderCategories,
  onReorderServices
}: ServicesCategoryAccordionProps) {
  // Por defecto todas las categorías expandidas
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = { uncategorized: true };
    categories.forEach(c => { initial[c.id] = true; });
    return initial;
  });

  // Estados de Drag and Drop
  const [draggedCatId, setDraggedCatId] = useState<string | null>(null);
  const [dragOverCatId, setDragOverCatId] = useState<string | null>(null);
  const [draggedServiceId, setDraggedServiceId] = useState<string | null>(null);
  const [dragOverServiceId, setDragOverServiceId] = useState<string | null>(null);
  const [dragOverCatForService, setDragOverCatForService] = useState<string | null>(null);

  const toggleCategory = (catId: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [catId]: !(prev[catId] ?? true)
    }));
  };

  // Filtrado por buscador
  const query = searchQuery.trim().toLowerCase();
  const filteredServices = services.filter(s =>
    !query ||
    s.name.toLowerCase().includes(query) ||
    s.category.toLowerCase().includes(query) ||
    (s.description && s.description.toLowerCase().includes(query))
  );

  // Agrupar por categoría ordenando por orderIndex
  const servicesByCategory = categories.map(cat => ({
    category: cat,
    items: filteredServices
      .filter(s => s.categoryId === cat.id || s.category.toLowerCase() === cat.name.toLowerCase())
      .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
  }));

  // Servicios sin categoría
  const knownCatIds = new Set(categories.map(c => c.id));
  const knownCatNames = new Set(categories.map(c => c.name.toLowerCase()));
  const uncategorizedItems = filteredServices
    .filter(
      s => (!s.categoryId || !knownCatIds.has(s.categoryId)) && !knownCatNames.has(s.category.toLowerCase())
    )
    .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));

  // --- Handlers de Reordenamiento de Categorías ---
  const handleCatDragStart = (e: React.DragEvent, catId: string) => {
    if (query) return;
    e.dataTransfer.setData('type', 'CATEGORY');
    e.dataTransfer.setData('id', catId);
    e.dataTransfer.effectAllowed = 'move';
    setDraggedCatId(catId);
  };

  const handleCatDragOver = (e: React.DragEvent, catId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedCatId && draggedCatId !== catId) {
      if (dragOverCatId !== catId) setDragOverCatId(catId);
    } else if (draggedServiceId) {
      if (dragOverCatForService !== catId) setDragOverCatForService(catId);
    }
  };

  const handleCatDrop = (e: React.DragEvent, targetCatId: string) => {
    e.preventDefault();
    if (draggedCatId && draggedCatId !== targetCatId) {
      const fromIdx = categories.findIndex(c => c.id === draggedCatId);
      const toIdx = categories.findIndex(c => c.id === targetCatId);
      if (fromIdx >= 0 && toIdx >= 0) {
        const next = [...categories];
        const [moved] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, moved);
        const reordered = next.map((cat, idx) => ({
          ...cat,
          orderIndex: idx + 1
        }));
        onReorderCategories?.(reordered);
      }
    } else if (draggedServiceId) {
      handleServiceDropOnCategory(draggedServiceId, targetCatId);
    }
    clearAllDragStates();
  };

  // --- Handlers de Reordenamiento de Servicios ---
  const handleServiceDragStart = (e: React.DragEvent, srvId: string) => {
    if (query) return;
    e.stopPropagation();
    e.dataTransfer.setData('type', 'SERVICE');
    e.dataTransfer.setData('id', srvId);
    e.dataTransfer.effectAllowed = 'move';
    setDraggedServiceId(srvId);
  };

  const handleServiceDragOver = (e: React.DragEvent, srvId: string) => {
    if (!draggedServiceId || draggedServiceId === srvId) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverServiceId !== srvId) setDragOverServiceId(srvId);
  };

  const handleServiceDrop = (e: React.DragEvent, targetSrvId: string, targetCatId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedServiceId || draggedServiceId === targetSrvId) {
      clearAllDragStates();
      return;
    }

    const draggedSrv = services.find(s => s.id === draggedServiceId);
    const targetSrv = services.find(s => s.id === targetSrvId);
    if (!draggedSrv || !targetSrv) {
      clearAllDragStates();
      return;
    }

    const targetCat = categories.find(c => c.id === targetCatId);
    const targetCatName = targetCat ? targetCat.name : (targetCatId === 'uncategorized' ? 'General' : draggedSrv.category);

    const remaining = services.filter(s => s.id !== draggedServiceId);
    const targetIdx = remaining.findIndex(s => s.id === targetSrvId);

    const updatedDragged: ServiceItem = {
      ...draggedSrv,
      categoryId: targetCatId === 'uncategorized' ? null : targetCatId,
      category: targetCatName
    };

    if (targetIdx >= 0) {
      remaining.splice(targetIdx, 0, updatedDragged);
    } else {
      remaining.push(updatedDragged);
    }

    // Reasignar orden secuencial por categoría
    const catCounters: Record<string, number> = {};
    const reordered = remaining.map(s => {
      const catKey = s.categoryId || 'uncategorized';
      catCounters[catKey] = (catCounters[catKey] ?? 0) + 1;
      return {
        ...s,
        orderIndex: catCounters[catKey]
      };
    });

    onReorderServices?.(reordered);
    clearAllDragStates();
  };

  const handleServiceDropOnCategory = (srvId: string, targetCatId: string) => {
    const draggedSrv = services.find(s => s.id === srvId);
    if (!draggedSrv) return;

    const targetCat = categories.find(c => c.id === targetCatId);
    const targetCatName = targetCat ? targetCat.name : (targetCatId === 'uncategorized' ? 'General' : draggedSrv.category);

    const remaining = services.filter(s => s.id !== srvId);
    const updatedDragged: ServiceItem = {
      ...draggedSrv,
      categoryId: targetCatId === 'uncategorized' ? null : targetCatId,
      category: targetCatName
    };

    const lastIdx = remaining.map(s => s.categoryId || 'uncategorized').lastIndexOf(targetCatId);
    if (lastIdx >= 0) {
      remaining.splice(lastIdx + 1, 0, updatedDragged);
    } else {
      remaining.push(updatedDragged);
    }

    const catCounters: Record<string, number> = {};
    const reordered = remaining.map(s => {
      const catKey = s.categoryId || 'uncategorized';
      catCounters[catKey] = (catCounters[catKey] ?? 0) + 1;
      return {
        ...s,
        orderIndex: catCounters[catKey]
      };
    });

    onReorderServices?.(reordered);
    clearAllDragStates();
  };

  const clearAllDragStates = () => {
    setDraggedCatId(null);
    setDragOverCatId(null);
    setDraggedServiceId(null);
    setDragOverServiceId(null);
    setDragOverCatForService(null);
  };

  return (
    <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm overflow-hidden">
      {/* Table Header */}
      <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3.5 bg-slate-50/75 border-b border-slate-200/80 text-[11px] font-bold uppercase tracking-wider text-slate-500 select-none">
        <div className="col-span-5 pl-7">Servicio</div>
        <div className="col-span-1 text-right">Lista</div>
        <div className="col-span-2 text-right">Efectivo</div>
        <div className="col-span-1 text-center">Duración</div>
        <div className="col-span-1 text-center">Puntos</div>
        <div className="col-span-1 text-center">Profesionales</div>
        <div className="col-span-1 text-right">Acciones</div>
      </div>

      {/* Categories & Services */}
      <div className="divide-y divide-slate-100">
        {servicesByCategory.map(({ category, items }, catIndex) => {
          const isExpanded = expandedCategories[category.id] ?? true;
          const isCatDragged = draggedCatId === category.id;
          const isCatOver = dragOverCatId === category.id;
          const isServiceHoveringCat = dragOverCatForService === category.id;

          return (
            <div
              key={category.id}
              onDragOver={(e) => handleCatDragOver(e, category.id)}
              onDrop={(e) => handleCatDrop(e, category.id)}
              className={`transition-all duration-150 ${
                isCatDragged
                  ? 'opacity-30 bg-emerald-50/60 border-2 border-dashed border-emerald-400'
                  : isCatOver
                  ? 'border-t-4 border-t-emerald-600 bg-emerald-50/40 shadow-xs'
                  : isServiceHoveringCat
                  ? 'ring-2 ring-emerald-500/60 bg-emerald-50/20'
                  : ''
              }`}
            >
              {/* Category Accordion Header */}
              <div
                onClick={() => toggleCategory(category.id)}
                className="flex items-center justify-between px-5 sm:px-6 py-4 hover:bg-slate-50/70 cursor-pointer select-none transition-colors group"
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  {/* Grip Handle para mover categoría */}
                  <div
                    draggable={!query}
                    onDragStart={(e) => handleCatDragStart(e, category.id)}
                    onDragEnd={clearAllDragStates}
                    onClick={(e) => e.stopPropagation()}
                    className={`p-1.5 -ml-1 rounded-md text-slate-300 group-hover:text-slate-500 hover:text-emerald-700 hover:bg-emerald-50 transition-colors ${
                      query ? 'cursor-not-allowed opacity-30' : 'cursor-grab active:cursor-grabbing'
                    }`}
                    title={query ? 'Búsqueda activa: no se puede reordenar' : 'Arrastrar categoría de arriba abajo'}
                  >
                    <GripVertical className="w-4 h-4" />
                  </div>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCategory(category.id);
                    }}
                    className="p-1 rounded-md text-slate-400 group-hover:text-slate-700 transition-colors"
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>

                  {/* Thumbnail */}
                  <div className="w-10 h-10 rounded-xl overflow-hidden bg-slate-100 border border-slate-200/60 shrink-0">
                    <img
                      src={category.coverUrl || 'https://images.unsplash.com/photo-1599351431202-1e0f0137899a?w=100&h=100&fit=crop'}
                      alt={category.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-bold text-slate-900 group-hover:text-emerald-700 transition-colors">
                        {category.name}
                      </h3>
                      <span className="text-[10px] font-semibold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                        #{catIndex + 1}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400">
                      {items.length} {items.length === 1 ? 'servicio' : 'servicios'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {onEditCategory && (
                    <button
                      type="button"
                      draggable={false}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditCategory(category);
                      }}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                      title="Editar categoría"
                    >
                      <SlidersHorizontal className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Services Rows */}
              {isExpanded && (
                <div className="divide-y divide-slate-100 bg-slate-50/30">
                  {items.length === 0 ? (
                    <div
                      onDragOver={(e) => handleCatDragOver(e, category.id)}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (draggedServiceId) handleServiceDropOnCategory(draggedServiceId, category.id);
                      }}
                      className={`py-6 px-14 text-xs italic transition-colors ${
                        isServiceHoveringCat
                          ? 'bg-emerald-100/70 text-emerald-800 font-semibold border-2 border-dashed border-emerald-400 rounded-xl m-3 text-center'
                          : 'text-slate-400'
                      }`}
                    >
                      {isServiceHoveringCat
                        ? 'Soltar aquí para mover servicio a esta categoría'
                        : 'No hay servicios registrados en esta categoría.'}
                    </div>
                  ) : (
                    items.map(service => {
                      const isServiceDragged = draggedServiceId === service.id;
                      const isServiceOver = dragOverServiceId === service.id;

                      return (
                        <div
                          key={service.id}
                          draggable={!query}
                          onDragStart={(e) => handleServiceDragStart(e, service.id)}
                          onDragOver={(e) => handleServiceDragOver(e, service.id)}
                          onDrop={(e) => handleServiceDrop(e, service.id, category.id)}
                          onDragEnd={clearAllDragStates}
                          className={`grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 px-5 sm:px-6 py-3.5 items-center hover:bg-white transition-all group ${
                            isServiceDragged
                              ? 'opacity-30 bg-emerald-50/60 border-y border-dashed border-emerald-400'
                              : isServiceOver
                              ? 'border-t-2 border-t-emerald-600 bg-emerald-50/60 shadow-xs'
                              : ''
                          }`}
                        >
                          {/* Servicio con Grip Handle */}
                          <div className="md:col-span-5 flex items-center gap-2 pl-4 sm:pl-6">
                            <div
                              onClick={(e) => e.stopPropagation()}
                              className={`p-1 -ml-1 text-slate-300 group-hover:text-slate-500 hover:text-emerald-700 rounded transition-colors shrink-0 ${
                                query ? 'cursor-not-allowed opacity-30' : 'cursor-grab active:cursor-grabbing'
                              }`}
                              title={query ? 'Búsqueda activa: no se puede reordenar' : 'Arrastrar servicio de arriba abajo'}
                            >
                              <GripVertical className="w-3.5 h-3.5" />
                            </div>

                            <div className="w-9 h-9 rounded-lg bg-emerald-50 border border-emerald-100/80 flex items-center justify-center shrink-0 overflow-hidden">
                              {service.imageUrl ? (
                                <img
                                  src={service.imageUrl}
                                  alt={service.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <Scissors className="w-4 h-4 text-emerald-600" />
                              )}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                <p className="text-xs font-bold text-slate-900 truncate">
                                  {service.name}
                                </p>
                                {service.isFeatured && (
                                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-50 text-amber-800 border border-amber-200/90 rounded text-[9px] font-bold shrink-0">
                                    <Star className="w-2.5 h-2.5 fill-amber-500 text-amber-500" />
                                    <span>Destacado</span>
                                  </span>
                                )}
                              </div>
                              {service.description && (
                                <p className="text-[11px] text-slate-400 truncate max-w-sm">
                                  {service.description}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Precio Lista */}
                          <div className="md:col-span-1 text-right text-xs text-slate-500 font-medium">
                            ${service.listPrice.toLocaleString('es-AR')}
                          </div>

                          {/* Precio Efectivo */}
                          <div className="md:col-span-2 text-right">
                            <span className="inline-block px-2.5 py-1 bg-emerald-50 border border-emerald-100/80 rounded-lg text-xs font-bold text-emerald-700 shadow-2xs">
                              ${service.cashPrice.toLocaleString('es-AR')}
                            </span>
                          </div>

                          {/* Duración */}
                          <div className="md:col-span-1 text-center">
                            <span className="inline-flex items-center gap-1 text-xs text-slate-600 font-medium bg-slate-100/80 px-2 py-0.5 rounded-md">
                              <Clock className="w-3 h-3 text-slate-400" />
                              {service.duration}m
                            </span>
                          </div>

                          {/* Puntos */}
                          <div className="md:col-span-1 text-center">
                            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md">
                              <Sparkles className="w-3 h-3 text-amber-500" />
                              {Math.round(service.cashPrice * 0.001)} pts
                            </span>
                          </div>

                          {/* Profesionales */}
                          <div className="md:col-span-1 flex items-center justify-center -space-x-1.5">
                            {service.staff && service.staff.length > 0 ? (
                              service.staff.map(st => (
                                <div
                                  key={st.id}
                                  title={st.name}
                                  style={{ backgroundColor: st.colorHex || '#10b981' }}
                                  className="w-6 h-6 rounded-full text-white text-[10px] font-bold flex items-center justify-center border-2 border-white shadow-xs"
                                >
                                  {st.name.charAt(0)}
                                </div>
                              ))
                            ) : (
                              <span className="text-[11px] text-slate-400">Todos</span>
                            )}
                          </div>

                          {/* Acciones */}
                          <div className="md:col-span-1 flex items-center justify-end gap-1">
                            {onToggleFeaturedService && (
                              <button
                                type="button"
                                draggable={false}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={() => onToggleFeaturedService(service)}
                                className={`p-1.5 rounded-lg transition-colors ${
                                  service.isFeatured
                                    ? 'text-amber-500 bg-amber-50 hover:bg-amber-100'
                                    : 'text-slate-300 hover:text-amber-500 hover:bg-slate-50'
                                }`}
                                title={service.isFeatured ? 'Quitar de destacados' : 'Marcar como servicio destacado (aparece en portada)'}
                              >
                                <Star className={`w-3.5 h-3.5 ${service.isFeatured ? 'fill-amber-500' : ''}`} />
                              </button>
                            )}
                            <button
                              type="button"
                              draggable={false}
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={() => onEditService(service)}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-700 hover:bg-emerald-50 transition-colors"
                              title="Editar servicio"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              draggable={false}
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={() => onDeleteService(service.id)}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                              title="Eliminar servicio"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}

                  {/* Drop zone al final de la categoría cuando se arrastra un servicio */}
                  {draggedServiceId && items.length > 0 && (
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (dragOverCatForService !== category.id) setDragOverCatForService(category.id);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (draggedServiceId) handleServiceDropOnCategory(draggedServiceId, category.id);
                      }}
                      className={`px-6 py-2 border-t border-dashed transition-colors text-center text-[11px] font-medium select-none ${
                        isServiceHoveringCat
                          ? 'border-emerald-500 bg-emerald-100/60 text-emerald-800'
                          : 'border-slate-200 bg-slate-50/50 text-slate-500 hover:bg-emerald-50/60 hover:text-emerald-700 hover:border-emerald-300'
                      }`}
                    >
                      + Soltar al final de {category.name}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Uncategorized Items */}
        {uncategorizedItems.length > 0 && (
          <div
            onDragOver={(e) => handleCatDragOver(e, 'uncategorized')}
            onDrop={(e) => handleCatDrop(e, 'uncategorized')}
            className={`transition-colors ${
              dragOverCatForService === 'uncategorized'
                ? 'ring-2 ring-emerald-500/60 bg-emerald-50/20'
                : ''
            }`}
          >
            <div
              onClick={() => toggleCategory('uncategorized')}
              className="flex items-center justify-between px-5 sm:px-6 py-4 hover:bg-slate-50/70 cursor-pointer select-none transition-colors group"
            >
              <div className="flex items-center gap-3.5">
                <button
                  type="button"
                  className="p-1 rounded-md text-slate-400 group-hover:text-slate-700 transition-colors"
                >
                  {expandedCategories['uncategorized'] ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
                <div>
                  <h3 className="text-sm font-bold text-slate-700">Sin Categoría</h3>
                  <p className="text-xs text-slate-400">
                    {uncategorizedItems.length} servicios
                  </p>
                </div>
              </div>
            </div>

            {expandedCategories['uncategorized'] && (
              <div className="divide-y divide-slate-100 bg-slate-50/30">
                {uncategorizedItems.map(service => {
                  const isServiceDragged = draggedServiceId === service.id;
                  const isServiceOver = dragOverServiceId === service.id;

                  return (
                    <div
                      key={service.id}
                      draggable={!query}
                      onDragStart={(e) => handleServiceDragStart(e, service.id)}
                      onDragOver={(e) => handleServiceDragOver(e, service.id)}
                      onDrop={(e) => handleServiceDrop(e, service.id, 'uncategorized')}
                      onDragEnd={clearAllDragStates}
                      className={`grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 px-5 sm:px-6 py-3.5 items-center hover:bg-white transition-all group ${
                        isServiceDragged
                          ? 'opacity-30 bg-emerald-50/60 border-y border-dashed border-emerald-400'
                          : isServiceOver
                          ? 'border-t-2 border-t-emerald-600 bg-emerald-50/60 shadow-xs'
                          : ''
                      }`}
                    >
                      <div className="md:col-span-5 flex items-center gap-2 pl-4 sm:pl-6">
                        <div
                          onClick={(e) => e.stopPropagation()}
                          className={`p-1 -ml-1 text-slate-300 group-hover:text-slate-500 hover:text-emerald-700 rounded transition-colors shrink-0 ${
                            query ? 'cursor-not-allowed opacity-30' : 'cursor-grab active:cursor-grabbing'
                          }`}
                          title={query ? 'Búsqueda activa: no se puede reordenar' : 'Arrastrar servicio de arriba abajo'}
                        >
                          <GripVertical className="w-3.5 h-3.5" />
                        </div>

                        <div className="w-9 h-9 rounded-lg bg-emerald-50 border border-emerald-100/80 flex items-center justify-center shrink-0 overflow-hidden">
                          <Scissors className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="text-xs font-bold text-slate-900 truncate">
                              {service.name}
                            </p>
                            {service.isFeatured && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-50 text-amber-800 border border-amber-200/90 rounded text-[9px] font-bold shrink-0">
                                <Star className="w-2.5 h-2.5 fill-amber-500 text-amber-500" />
                                <span>Destacado</span>
                              </span>
                            )}
                          </div>
                          {service.description && (
                            <p className="text-[11px] text-slate-400 truncate max-w-sm">
                              {service.description}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="md:col-span-1 text-right text-xs text-slate-500 font-medium">
                        ${service.listPrice.toLocaleString('es-AR')}
                      </div>

                      <div className="md:col-span-2 text-right">
                        <span className="inline-block px-2.5 py-1 bg-emerald-50 border border-emerald-100/80 rounded-lg text-xs font-bold text-emerald-700">
                          ${service.cashPrice.toLocaleString('es-AR')}
                        </span>
                      </div>

                      <div className="md:col-span-1 text-center">
                        <span className="inline-flex items-center gap-1 text-xs text-slate-600 font-medium bg-slate-100/80 px-2 py-0.5 rounded-md">
                          <Clock className="w-3 h-3 text-slate-400" />
                          {service.duration}m
                        </span>
                      </div>

                      <div className="md:col-span-1 text-center">
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md">
                          <Sparkles className="w-3 h-3 text-amber-500" />
                          {Math.round(service.cashPrice * 0.001)} pts
                        </span>
                      </div>

                      <div className="md:col-span-1 flex items-center justify-center -space-x-1.5">
                        <span className="text-[11px] text-slate-400">Todos</span>
                      </div>

                      <div className="md:col-span-1 flex items-center justify-end gap-1">
                        {onToggleFeaturedService && (
                          <button
                            type="button"
                            draggable={false}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={() => onToggleFeaturedService(service)}
                            className={`p-1.5 rounded-lg transition-colors ${
                              service.isFeatured
                                ? 'text-amber-500 bg-amber-50 hover:bg-amber-100'
                                : 'text-slate-300 hover:text-amber-500 hover:bg-slate-50'
                            }`}
                            title={service.isFeatured ? 'Quitar de destacados' : 'Marcar como servicio destacado (aparece en portada)'}
                          >
                            <Star className={`w-3.5 h-3.5 ${service.isFeatured ? 'fill-amber-500' : ''}`} />
                          </button>
                        )}
                        <button
                          type="button"
                          draggable={false}
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={() => onEditService(service)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-700 hover:bg-emerald-50 transition-colors"
                          title="Editar servicio"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          draggable={false}
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={() => onDeleteService(service.id)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                          title="Eliminar servicio"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
